import type { GeofenceFeature } from './types'

/**
 * Ray-casting containment against the Earth-project polygons.
 *
 * DISPLAY ONLY, and temporary. GeofenceProcessor already decides containment
 * authoritatively -- with hysteresis, so a GPS fix wandering 200 m cannot open and close
 * a hub visit -- but its output is dark until geofence.definition is published. This is
 * the stopgap that lets the map show "12 at Dhar Plant" today, and it is deleted in
 * phase 4 when the console consumes geofence.event instead. Nothing operational may be
 * derived from it: no dwell, no TAT, no alerts.
 *
 * Polygons here are 5-7 vertices and there are 25 of them, so the naive O(n·m) sweep over
 * 40 vehicles costs microseconds. A spatial index would be more code and less clarity.
 */

/** Standard even-odd ray cast. `ring` is GeoJSON order: [lon, lat]. */
export function pointInRing(lon: number, lat: number, ring: readonly (readonly number[])[]): boolean {
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i]![0]!, yi = ring[i]![1]!
    const xj = ring[j]![0]!, yj = ring[j]![1]!
    // Strict/non-strict pair straddles the ray so a vertex is counted exactly once.
    const straddles = yi > lat !== yj > lat
    if (straddles && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside
  }
  return inside
}

/** Cheap reject before the ray cast. bbox is the GeoJSON-order envelope of the ring. */
function inBbox(lon: number, lat: number, b: readonly number[]): boolean {
  return lon >= b[0]! && lon <= b[2]! && lat >= b[1]! && lat <= b[3]!
}

export function ringBbox(ring: readonly (readonly number[])[]): [number, number, number, number] {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
  for (const p of ring) {
    if (p[0]! < minX) minX = p[0]!
    if (p[0]! > maxX) maxX = p[0]!
    if (p[1]! < minY) minY = p[1]!
    if (p[1]! > maxY) maxY = p[1]!
  }
  return [minX, minY, maxX, maxY]
}

export interface FenceIndex {
  id: string
  name: string
  type: string
  bbox: [number, number, number, number]
  ring: readonly (readonly number[])[]
}

export function indexFences(features: readonly GeofenceFeature[]): FenceIndex[] {
  return features.flatMap((f) => {
    const ring = f.geometry?.coordinates?.[0]
    if (!ring || ring.length < 4) return []
    return [{
      id: f.properties.geofence_id,
      name: f.properties.name,
      type: String(f.properties.type),
      bbox: ringBbox(ring),
      ring,
    }]
  })
}

/**
 * The innermost fence containing the point, by bbox area. Gates and queues nest inside
 * plants in the Earth project, and "inside Dhar Gate 2 Queue" is the useful answer --
 * "inside Dhar Plant" is technically also true and operationally useless.
 */
export function locate(lon: number, lat: number, fences: readonly FenceIndex[]): FenceIndex | null {
  let best: FenceIndex | null = null
  let bestArea = Infinity
  for (const f of fences) {
    if (!inBbox(lon, lat, f.bbox)) continue
    if (!pointInRing(lon, lat, f.ring)) continue
    const area = (f.bbox[2] - f.bbox[0]) * (f.bbox[3] - f.bbox[1])
    if (area < bestArea) { best = f; bestArea = area }
  }
  return best
}

/** fence id -> vehicle ids currently inside it. Vehicles with no fix are skipped. */
export function countByFence(
  vehicles: readonly { vehicle_id: string; lat: number | null; lon: number | null }[],
  fences: readonly FenceIndex[],
): Map<string, string[]> {
  const out = new Map<string, string[]>()
  for (const v of vehicles) {
    if (v.lat == null || v.lon == null) continue
    const f = locate(v.lon, v.lat, fences)
    if (!f) continue
    const list = out.get(f.id)
    if (list) list.push(v.vehicle_id)
    else out.set(f.id, [v.vehicle_id])
  }
  return out
}
